import { LoadingOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Empty, message, Select, Spin } from 'antd';
import _ from 'lodash';
import React, { useState } from 'react';

import { fetchAPI } from '../utils/api';

/**
 * This is dropdown for project codes, this is for used Form.Item Component
 * the value is string
 */
const ProjectCodesDropdown = (props: {
  visible: boolean;
  disabled: boolean;
  onChange: (value: string) => void;
  entityId: number;
  additionalFilters?: unknown;
}) => {
  const [filters, setFilters] = useState<{
    q: string;
    page: number;
    open: boolean;
    selected?: string;
  }>({
    q: '',
    page: 1,
    open: false,
    selected: null,
  });

  const projectCodes = useQuery<{
    items: string[];
    page: number;
    per_page: number;
    total: number;
    loading: boolean;
  }>(
    [
      'project-codes/list-for-filter',
      filters,
      props.visible,
      props.entityId,
      props.additionalFilters,
    ],
    async () => {
      if (!props.visible || !props.entityId) {
        return {
          items: [],
          page: filters.page,
          per_page: 10,
          total: 0,
          loading: false,
        };
      }
      try {
        const resp = await fetchAPI('project-codes', {
          method: 'POST',
          version: '2.0',
          payload: {
            q: filters.q,
            page: filters.page,
            per_page: 10,
            entity_id: props.entityId,
            ...(typeof props.additionalFilters === 'object'
              ? props.additionalFilters
              : {}),
          },
        });

        return resp;
      } catch (error) {
        message.error('Failed get project codes');
        return {
          items: [],
          page: filters.page,
          per_page: 10,
          total: 0,
          loading: false,
        };
      }
    },
    {
      staleTime: 10 * 60 * 1000,
      placeholderData: {
        items: [],
        page: filters.page,
        per_page: 10,
        total: 0,
        loading: true,
      },
    }
  );

  const pcLoading = projectCodes.isLoading || projectCodes.data.loading;

  return (
    <Select
      placeholder="Select Project Code"
      open={filters.open}
      onDropdownVisibleChange={vis => setFilters(f => ({ ...f, open: vis }))}
      showSearch
      filterOption={false}
      onSearch={_.debounce(
        value => setFilters(f => ({ ...f, page: 1, q: value })),
        350
      )}
      disabled={props.disabled}
      onSelect={value => {
        if (value === '-1') {
          setFilters(f => ({ ...f, page: f.page - 1 }));
          setTimeout(() => {
            setFilters(f => ({ ...f, open: true }));
          }, 150);
          return;
        }
        if (value === '-2') {
          setFilters(f => ({ ...f, page: f.page + 1 }));
          setTimeout(() => {
            setFilters(f => ({ ...f, open: true }));
          }, 150);
          return;
        }

        props.onChange(String(value));
        const selectedProjectCode = projectCodes.data.items.find(
          str => str === value
        );
        setFilters(f => ({ ...f, selected: selectedProjectCode }));
      }}
      notFoundContent={
        pcLoading ? (
          <Spin
            indicator={<LoadingOutlined />}
            style={{ width: '100%', padding: 15 }}
          />
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="Entities not found :("
          />
        )
      }
    >
      {!pcLoading && filters.page > 1 && (
        <Select.Option value={'-1'}>
          <span style={{ width: '100%', textAlign: 'left' }}>{`<< Prev`}</span>
        </Select.Option>
      )}
      {projectCodes.data.items.map((str, idx) => {
        return (
          <Select.Option key={idx} value={str}>
            {str}
          </Select.Option>
        );
      })}
      {!pcLoading &&
        filters.page * projectCodes.data.per_page < projectCodes.data.total && (
          <Select.Option value={'-2'}>
            <span
              style={{ width: '100%', textAlign: 'right' }}
            >{`Next >>`}</span>
          </Select.Option>
        )}
    </Select>
  );
};

export default ProjectCodesDropdown;
